/**
 * Screen Orientation Utility
 * Handles locking and unlocking screen orientation for Capacitor apps
 *
 * iOS Native: Uses custom ScreenOrientationPlugin (Swift) for dynamic orientation locking
 * Android Native: Orientation locking disabled (plugin not being discovered by Capacitor)
 * Web/Emulator: Uses browser Screen Orientation API or Capacitor plugin fallback
 *
 * NOTE:
 * - For iOS, the custom plugin must be added to the Xcode project.
 *   See ios/App/App/ScreenOrientationPlugin.swift for implementation details.
 * - For Android, orientation locking is currently disabled.
 *   Custom plugin at android/app/src/main/java/app/mobile/hogc/ScreenOrientationPlugin.java
 *   is not being discovered by Capacitor, so we skip orientation calls on Android.
 */

import { isIosNativeOnly, isTablet } from './platform-check';
import { lockOrientationIOS, unlockOrientationIOS } from './ios-screen-orientation-bridge';

// Official Capacitor Screen Orientation plugin removed - using custom iOS plugin instead
// Android and web use browser API fallback

/**
 * Lock screen orientation
 * @param {string} orientation - 'portrait' or 'landscape'
 * @returns {Promise<void>}
 */
export async function lockOrientation(orientation) {
  // Skip orientation lock on iPad - plugin doesn't exist and iPad should support all orientations
  if (isTablet()) {
    console.log('[ScreenOrientation] Skipping orientation lock on iPad/iPadOS');
    return;
  }

  // Forward to native log
  if (window.Android && window.Android.log) {
    window.Android.log(`[ScreenOrientation] lockOrientation called with: ${orientation}`);
  }

  // Try custom native iOS plugin first (iPhone only)
  if (isIosNativeOnly()) {
    const success = await lockOrientationIOS(orientation);
    if (success) {
      return;
    }
    // If iOS plugin fails, don't try Capacitor plugin (we removed it to avoid duplicate class conflict)
    // Just continue to browser API fallback
  }

  // Fallback to browser Screen Orientation API (for web/emulator)
  // Note: iOS native doesn't support screen.orientation API
  if (isIosNativeOnly()) {
    console.warn(
      '[ScreenOrientation] ⚠️ iOS native plugin failed. Browser API not available on iOS native.',
    );
    return;
  }

  if (!screen?.orientation) {
    console.warn('[ScreenOrientation] ⚠️ Screen Orientation API not available');
    return;
  }

  try {
    const orientationType = orientation === 'landscape' ? 'landscape-primary' : 'portrait-primary';

    await screen.orientation.lock(orientationType);
  } catch (error) {
    // Fallback: try using the legacy API
    try {
      if (screen.lockOrientation) {
        screen.lockOrientation(orientation);
      } else if (screen.mozLockOrientation) {
        screen.mozLockOrientation(orientation);
      } else if (screen.msLockOrientation) {
        screen.msLockOrientation(orientation);
      }
    } catch (fallbackError) {
      console.warn(
        '[ScreenOrientation] Failed to lock orientation:',
        fallbackError?.message || 'Unknown error',
      );
    }
  }
}

/**
 * Unlock screen orientation
 * @returns {Promise<void>}
 */
export async function unlockOrientation() {
  // Skip orientation unlock on iPad - plugin doesn't exist and iPad should support all orientations
  if (isTablet()) {
    console.log('[ScreenOrientation] Skipping orientation unlock on iPad/iPadOS');
    return;
  }

  // Try custom native iOS plugin first (iPhone only)
  if (isIosNativeOnly()) {
    const success = await unlockOrientationIOS();
    if (success) {
      return;
    }
    // If iOS plugin fails, don't try Capacitor plugin (we removed it to avoid duplicate class conflict)
    // Just continue to browser API fallback
  }

  // Fallback to browser Screen Orientation API (for web/emulator)
  // Note: iOS native doesn't support screen.orientation API
  if (isIosNativeOnly()) {
    console.warn(
      '[ScreenOrientation] ⚠️ iOS native plugin failed. Browser API not available on iOS native.',
    );
    return;
  }

  if (!screen?.orientation) {
    console.warn('[ScreenOrientation] ⚠️ Screen Orientation API not available');
    return;
  }

  try {
    await screen.orientation.unlock();
  } catch (error) {
    // Fallback: try using the legacy API
    try {
      if (screen.unlockOrientation) {
        screen.unlockOrientation();
      } else if (screen.mozUnlockOrientation) {
        screen.mozUnlockOrientation();
      } else if (screen.msUnlockOrientation) {
        screen.msUnlockOrientation();
      }
    } catch (fallbackError) {
      console.warn(
        '[ScreenOrientation] Failed to unlock orientation:',
        fallbackError?.message || 'Unknown error',
      );
    }
  }
}

/**
 * Lock screen orientation to portrait
 * Convenience function that calls lockOrientation('portrait')
 * @returns {Promise<void>}
 */
export async function lockToPortrait() {
  return lockOrientation('portrait');
}

/**
 * Check if a route path is a library-related route that should allow landscape orientation
 * @param {string} path - The route path to check
 * @returns {boolean} - True if the route is a library route
 */
export function isLibraryRoute(path) {
  if (!path) return false;
  // Normalize path - remove leading/trailing slashes and convert to lowercase for comparison
  const normalizedPath = path.toLowerCase().replace(/^\/+|\/+$/g, '');
  // Check if path starts with 'library'
  return normalizedPath.startsWith('library/sermon');
}

/**
 * Check if a route path is an absolutely-related route
 * @param {string} path - The route path to check
 * @returns {boolean} - True if the route is an absolutely route
 */
export function isAbsolutelyRoute(path) {
  if (!path) return false;
  // Normalize path - remove leading/trailing slashes and convert to lowercase for comparison
  const normalizedPath = path.toLowerCase().replace(/^\/+|\/+$/g, '');
  // Check if path starts with 'absolutely'
  return normalizedPath.startsWith('absolutely');
}

/**
 * Check if an absolutely route should be locked to landscape (video step pages and complete page)
 * @param {string} path - The route path to check
 * @returns {boolean} - True if the route is a video step page or complete page that should be landscape
 */
export function isAbsolutelyVideoStepRoute(path) {
  if (!path) return false;
  // Check if path matches pattern: /absolutely/chapter/:id/:stepId or /absolutely/chapter/:id/complete
  const normalizedPath = path.toLowerCase().replace(/^\/+|\/+$/g, '');
  const parts = normalizedPath.split('/');
  // Pattern: ['absolutely', 'chapter', ':id', ':stepId'] or ['absolutely', 'chapter', ':id', 'complete']
  // Should have 4 parts
  return parts.length === 4 && parts[0] === 'absolutely' && parts[1] === 'chapter';
}

/**
 * Determine the orientation action needed for a route
 * @param {string} path - The route path to check
 * @returns {string|null} - 'landscape', 'portrait', 'unlock', or null if no action needed
 */
export function getOrientationActionForRoute(path) {
  if (!path) return null;

  // Absolutely video step routes and complete page should be landscape
  // Pattern: /absolutely/chapter/:id/:stepId or /absolutely/chapter/:id/complete
  if (isAbsolutelyVideoStepRoute(path)) {
    return 'landscape';
  }

  // Other absolutely routes should be portrait
  if (isAbsolutelyRoute(path)) {
    return 'portrait';
  }

  // Library routes should be unlocked
  if (isLibraryRoute(path)) {
    return 'unlock';
  }

  // All other routes default to portrait
  return 'portrait';
}

/**
 * Check if screen orientation API is supported
 * @returns {Promise<boolean>}
 */
export async function isOrientationSupported() {
  // For iOS native, check if custom plugin is available
  if (isIosNativeOnly()) {
    return true; // Custom plugin is always available on iOS native
  }

  // Check browser API
  return !!(
    screen?.orientation ||
    screen?.lockOrientation ||
    screen?.mozLockOrientation ||
    screen?.msLockOrientation
  );
}
